import React, { useMemo, useState } from "react";
// import AppCard from "@/components/Common/appCard";
// import { Button } from "@/components/Common/button";
import { LoadingButton } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Globe2, Pencil, Eye, Text } from "lucide-react";
// import { getLabel, toDateOnly, visitProject } from "@/utilities/functions/common";
// import { GHG_REPORT_PURPOSE } from "@/utilities/constants/options.constants";
import { useTranslation } from "react-i18next";
import {
  calculateNxmapProgress,
  getLabel,
  normalizeNxmapDataForProgress,
  toDateOnly,
  visitProject,
  GHG_REPORT_PURPOSE,
} from "@/features/onboarding/utils";
import { Can } from "@/hooks/useCan/Can";
import { showToast } from "@/lib/utils/toast";

const NXMapCard = ({
  canVisitProjects = false,
  permissions,
  isUserLevel,
  baseYear,
  organizationBoundary,
  reportingYear,
  reportingStandard,
  data = {},
  onOpen,
}) => {
  const { t } = useTranslation();
  const notSetLabel = t("Not set");
  const [isVisitingNxmap, setIsVisitingNxmap] = useState(false);

  const progress = useMemo(() => {
    const normalized = normalizeNxmapDataForProgress(
      data,
      baseYear,
      organizationBoundary,
      reportingYear,
      reportingStandard,
    );
    return calculateNxmapProgress(normalized);
  }, [data, baseYear, organizationBoundary, reportingYear, reportingStandard]);
  const pct = Math.max(0, Math.min(100, Number(progress) || 0));
  const purposeLabel = useMemo(() => {
    const label = getLabel(data.ghgReportPurpose, GHG_REPORT_PURPOSE);
    return label ? t(label) : notSetLabel;
  }, [data.ghgReportPurpose, notSetLabel, t]);
  const thresholdValue = data.threshold ?? notSetLabel;
  const periodStart = toDateOnly(data.baseYearStart) || notSetLabel;
  const periodEnd = toDateOnly(data.baseYearEnd) || notSetLabel;

  const handleVisitNxmap = async () => {
    setIsVisitingNxmap(true);

    try {
      await visitProject("nxmap");
    } catch (error) {
      showToast.error(t("Unable to visit NXMap. Please try again."));
    } finally {
      setIsVisitingNxmap(false);
    }
  };

  const progressFooter = (
    <div className="flex items-center gap-3">
      <div className="h-1.5 w-full flex-1 overflow-hidden rounded-full bg-gradient-to-r from-emerald-400/20 to-blue-500/20">
        <div
          className="h-full bg-gradient-to-r from-emerald-400 to-blue-500 transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );

  return (
    // <AppCard
    //   className="overflow-hidden lg:col-span-1"
    //   title={t('NXMap - Reporting Configuration')}
    //   icon={Globe2}
    //   actions={
    //     <>
    //       <Text variant="value" className="hidden sm:inline">
    //         {progress}%
    //       </Text>
    //       <Button
    //         size="sm"
    //         variant="nxStartBlue"
    //         className="transition-transform duration-200 hover:-translate-y-0.5"
    //         onClick={() => onOpen('nxmap', data)}
    //       >
    //         <Pencil className="!h-3 !w-3" />
    //         <span className="hidden sm:inline">{t('Edit Info')}</span>
    //       </Button>
    //       <Button
    //         size="sm"
    //         variant="nxStartOrigin"
    //         className="transition-transform duration-200 hover:-translate-y-0.5"
    //         onClick={() => visitProject('nxmap')}
    //         // onClick={() => {
    //         //   if (typeof window !== 'undefined') {
    //         //     const win = window.open(
    //         //       'https://nxmapstg.evercomm.com.sg/login',
    //         //       '_blank',
    //         //       'noopener,noreferrer',
    //         //     );
    //         //     if (win) win.opener = null;
    //         //   }
    //         // }}
    //       >
    //         {t('Visit NXMap')}
    //       </Button>
    //     </>
    //   }
    //   progress={progress}
    // >
    //   <div className="my-5 grid grid-cols-1 gap-4 md:grid-cols-2">
    //     <div className="rounded-xl border p-3 dark:border-white/20">
    //       <div className="flex items-center justify-between py-1">
    //         <Text variant="label">{t('Purpose')}</Text>
    //         <Text variant="value">{purposeLabel}</Text>
    //       </div>
    //       <div className="flex items-center justify-between py-1">
    //         <Text variant="label">{t('Threshold')}</Text>
    //         <Text variant="value">{thresholdValue}</Text>
    //       </div>
    //     </div>
    //     <div className="rounded-xl border p-3 dark:border-white/20">
    //       <div className="flex items-center justify-between py-1">
    //         <Text variant="label">{t('Period Start')}</Text>
    //         <Text variant="value">{periodStart}</Text>
    //       </div>
    //       <div className="flex items-center justify-between py-1">
    //         <Text variant="label">{t('Period End')}</Text>
    //         <Text variant="value">{periodEnd}</Text>
    //       </div>
    //     </div>
    //   </div>
    // </AppCard>
    <Card className="rounded-radius pt-0">
      <CardHeader className="grid gap-3 rounded-t-lg bg-gradient-to-br from-slate-100 to-white px-4 py-4 dark:from-white/10 dark:to-white/5 sm:py-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <CardTitle className="flex items-start gap-2 text-base text-primary">
            <Globe2 size={20} />
            {t("NXMap - Reporting Configuration")}
          </CardTitle>
          <div className="hidden md:flex md:min-w-fit md:flex-col md:gap-3 md:items-end">
            <div className="md:flex md:w-auto md:flex-wrap md:items-center md:justify-end md:gap-2">
              <span className="text-sm font-semibold text-foreground">
                {pct}%
              </span>
              {/* <Can
                resource="company_information"
                action="update"
                enabled={!isUserLevel}
                userPermissions={permissions}
              >
                <Button
                  size="sm"
                  variant="secondary"
                  className="bg-primary text-white hover:bg-primary/90 dark:bg-primary dark:hover:bg-primary/90"
                  onClick={() => onOpen("nxmap", data)}
                  type="button"
                >
                  {isUserLevel ? (
                    <span>{t("View")}</span>
                  ) : (
                    <>
                      <Pencil className="!h-3 !w-3" />
                      <span>{t("Edit Info")}</span>
                    </>
                  )}
                </Button>
              </Can> */}

              <Can
                resource="onboard_setup"
                action="view"
                enabled={!isUserLevel}
                userPermissions={permissions}
              >
                <Button
                  size="sm"
                  variant="secondary"
                  className="bg-primary text-white hover:bg-primary/90 dark:bg-primary dark:hover:bg-primary/90"
                  onClick={() => onOpen("nxmap", data)}
                  type="button"
                >
                  <Eye className="!h-5 !w-5" />
                  <span className="hidden sm:inline">{t("View Info")}</span>
                </Button>
              </Can>

              {canVisitProjects ? (
                <LoadingButton
                  size="sm"
                  variant="secondary"
                  type="button"
                  loading={isVisitingNxmap}
                  className="bg-nxstart-origin text-white hover:bg-nxstart-origin/90 dark:bg-nxstart-origin dark:hover:bg-nxstart-origin/90"
                  onClick={handleVisitNxmap}
                >
                  {t("Visit NXMap")}
                </LoadingButton>
              ) : null}
            </div>
          </div>
        </div>
        <span className="text-sm font-semibold text-foreground md:hidden">
          {pct}%
        </span>
        {progressFooter}
        <div className="grid w-full grid-cols-2 gap-2 text-base text-primary md:hidden">
          <Can
            resource="company_information"
            action="update"
            enabled={!isUserLevel}
            userPermissions={permissions}
          >
            <Button
              size="sm"
              variant="secondary"
              className="w-full bg-primary text-white hover:bg-primary/90 dark:bg-primary dark:hover:bg-primary/90"
              onClick={() => onOpen("nxmap", data)}
              type="button"
            >
              {isUserLevel ? (
                <span>{t("View")}</span>
              ) : (
                <>
                  <Pencil className="!h-3 !w-3" />
                  <span>{t("Edit Info")}</span>
                </>
              )}
            </Button>
          </Can>

          {canVisitProjects ? (
            <LoadingButton
              size="sm"
              variant="secondary"
              type="button"
              loading={isVisitingNxmap}
              className="w-full bg-nxstart-origin text-white hover:bg-nxstart-origin/90 dark:bg-nxstart-origin dark:hover:bg-nxstart-origin/90"
              onClick={handleVisitNxmap}
            >
              {t("Visit NXMap")}
            </LoadingButton>
          ) : null}
        </div>
      </CardHeader>
      {/* <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border text-sm p-3 dark:border-white/20">
            <div className="mb-2 flex items-start justify-between gap-3">
              <p className="text-muted-foreground">{t("Purpose")}</p>
              <p className="text-right">{purposeLabel}</p>
            </div>
            <div className="flex items-start justify-between gap-3">
              <p className="text-muted-foreground">{t("Threshold")}</p>
              <p className="text-right">{thresholdValue}</p>
            </div>
          </div>
          <div className="rounded-xl border text-sm p-3 dark:border-white/20">
            <div className="mb-2 flex items-start justify-between gap-3">
              <p className="text-muted-foreground">{t("Period Start")}</p>
              <p className="text-right">{periodStart}</p>
            </div>
            <div className="flex items-start justify-between gap-3">
              <p className="text-muted-foreground">{t("Period End")}</p>
              <p className="text-right">{periodEnd}</p>
            </div>
          </div>
        </div>
      </CardContent> */}
    </Card>
  );
};

export default NXMapCard;
